import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  AlertTriangle,
  Clock,
  ArrowRight,
  ChevronLeft,
  Loader2,
  Flame,
} from "lucide-react";
import { getRejectionCoaching, acknowledgeRejectionCoaching } from "@/lib/api";

// ─── helpers ────────────────────────────────────────────────────────────────

const CTA_MAP = {
  "/app/resume": "/app/resume",
  "/app/interview": "/app/interview",
  "/app/practice": "/app/practice",
  "/app/skills": "/app/skills",
  "/app/learn": "/app/learn",
};

const resolvePath = (ctaPath) => CTA_MAP[ctaPath] ?? ctaPath;

const PRIORITY_STYLES = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-green-100 text-green-700 border-green-200",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" },
  }),
};

// ─── skeleton ────────────────────────────────────────────────────────────────

function Skeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-xl bg-stone-200 ${className}`}
    />
  );
}

function LoadingSkeleton() {
  return (
    <div className="container-x py-10 md:py-14 space-y-10">
      <Skeleton className="h-40 w-full rounded-3xl" />
      <div className="grid sm:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
      </div>
      <div className="space-y-4">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <Skeleton className="h-16" />
    </div>
  );
}

// ─── sub-components ──────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  const label = priority?.charAt(0).toUpperCase() + priority?.slice(1);
  const cls = PRIORITY_STYLES[priority] ?? PRIORITY_STYLES.medium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-semibold border ${cls}`}>
      {label}
    </span>
  );
}

function ScoreBar({ current, target }) {
  const pct = Math.min(100, Math.round((current / Math.max(target, 1)) * 100));
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center text-sm font-medium">
        <span className="text-stone-600">
          Your score: <span className="text-stone-900 font-mono">{current}</span>
        </span>
        <span className="text-stone-600">
          Target: <span className="font-mono text-[#C2410C]">{target}</span>
        </span>
      </div>
      <div className="h-3 rounded-full bg-stone-100 overflow-hidden border border-stone-200">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-[#C2410C] to-amber-500"
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </div>
      <p className="text-xs text-stone-500 font-mono">{pct}% of target reached</p>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function RejectionCoachingPage() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await getRejectionCoaching(applicationId);
        setData(res);
        // Auto-acknowledge on open
        try { await acknowledgeRejectionCoaching(applicationId); } catch (_) {}
        setAcknowledged(true);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [applicationId]);

  if (loading) return <LoadingSkeleton />;

  // No data yet
  if (!data) {
    return (
      <div className="container-x py-16 text-center">
        <div className="inline-flex p-4 rounded-2xl bg-amber-100 text-amber-600 mb-6">
          <Loader2 size={28} className="animate-spin" />
        </div>
        <h2 className="font-display text-3xl mb-3">Preparing your coaching analysis…</h2>
        <p className="text-stone-500 max-w-md mx-auto leading-relaxed">
          Our AI is reviewing your application and generating personalised insights. Check back in
          a few minutes.
        </p>
        <button onClick={() => navigate(-1)} className="btn-ghost mt-8 inline-flex items-center gap-2">
          <ChevronLeft size={16} /> Back
        </button>
      </div>
    );
  }

  const {
    job_title,
    company_name,
    what_went_well = [],
    gaps = [],
    next_steps = [],
    score_current,
    score_target,
    encouragement_quote,
  } = data;

  return (
    <div className="container-x py-10 md:py-14 space-y-12">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors"
      >
        <ChevronLeft size={16} /> Applications
      </button>

      {/* ── Hero ── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0}
        className="relative overflow-hidden rounded-3xl px-8 py-10 md:px-14 md:py-14"
        style={{
          background: "linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)",
          borderLeft: "4px solid #C2410C",
        }}
      >
        <div className="inline-flex items-center gap-2 mb-4">
          <Flame size={18} className="text-[#C2410C]" />
          <span className="overline text-[#C2410C]">Rejection Coaching</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight text-stone-900 mb-2">
          Let's turn this into fuel.
        </h1>
        {(job_title || company_name) && (
          <p className="text-stone-500 mt-3 text-base">
            Application for{" "}
            <span className="font-semibold text-stone-700">{job_title}</span>
            {company_name && (
              <> at <span className="font-semibold text-stone-700">{company_name}</span></>
            )}
          </p>
        )}
        <p className="mt-4 text-stone-600 max-w-xl leading-relaxed">
          Every rejection carries a signal. We've analysed yours — here's exactly what to do next.
        </p>
      </motion.div>

      {/* ── What went well ── */}
      {what_went_well.length > 0 && (
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          <h2 className="font-display text-2xl md:text-3xl mb-5">
            What went well
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {what_went_well.map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.15}
                className="glass rounded-2xl p-5 flex items-start gap-3"
              >
                <CheckCircle2 size={20} className="shrink-0 text-[#15803D] mt-0.5" />
                <p className="text-stone-700 text-sm leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}

      {/* ── Where to focus next ── */}
      {gaps.length > 0 && (
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={2}
        >
          <h2 className="font-display text-2xl md:text-3xl mb-5">
            Where to focus next
          </h2>
          <div className="space-y-4">
            {gaps.map((gap, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.12}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-500 shrink-0" />
                    <p className="font-semibold text-stone-900 text-sm">{gap.description}</p>
                  </div>
                  <PriorityBadge priority={gap.priority} />
                </div>

                {gap.action && (
                  <p className="text-stone-600 text-sm leading-relaxed mb-2">
                    <span className="font-medium text-stone-700">Action: </span>
                    {gap.action}
                  </p>
                )}

                {gap.timeframe && (
                  <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-4">
                    <Clock size={13} />
                    <span>{gap.timeframe}</span>
                  </div>
                )}

                {gap.cta_path && (
                  <button
                    onClick={() => navigate(resolvePath(gap.cta_path))}
                    className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5"
                  >
                    Get started <ArrowRight size={13} />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}

      {/* ── Score projection ── */}
      {score_current != null && score_target != null && (
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={3}
          className="glass rounded-2xl p-6 md:p-8"
        >
          <h2 className="font-display text-2xl mb-6">Score projection</h2>
          <ScoreBar current={score_current} target={score_target} />
        </motion.section>
      )}

      {/* ── Your next steps ── */}
      {next_steps.length > 0 && (
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={4}
        >
          <h2 className="font-display text-2xl md:text-3xl mb-5">Your next steps</h2>
          <ol className="space-y-4">
            {next_steps.map((step, i) => (
              <motion.li
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                className="flex items-start gap-4 glass rounded-2xl p-5"
              >
                {/* Step number */}
                <div className="shrink-0 w-8 h-8 rounded-full bg-stone-900 text-stone-50 flex items-center justify-center font-mono text-sm font-bold">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-semibold text-stone-900 text-sm">{step.title ?? step.description ?? step}</p>
                    {step.priority && <PriorityBadge priority={step.priority} />}
                  </div>
                  {step.description && step.title && (
                    <p className="text-stone-600 text-sm leading-relaxed mb-3">{step.description}</p>
                  )}
                  {step.cta_path && (
                    <button
                      onClick={() => navigate(resolvePath(step.cta_path))}
                      className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                    >
                      Go <ArrowRight size={12} />
                    </button>
                  )}
                </div>
              </motion.li>
            ))}
          </ol>
        </motion.section>
      )}

      {/* ── Encouragement quote ── */}
      {encouragement_quote && (
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={5}
          className="text-center py-8 px-6"
        >
          <p className="font-display text-2xl md:text-3xl italic text-stone-500 max-w-2xl mx-auto leading-relaxed">
            "{encouragement_quote}"
          </p>
        </motion.div>
      )}
    </div>
  );
}
